import React, { useContext, useEffect } from 'react';
import classnames from 'classnames';
import {
  NoteTriggerHandler,
  PlaybackState,
  MeasuresProvider,
  AdvanceHandler,
} from '../modules/tone';
import { Measure } from '../modules/vex';
import {
  Transport,
  startPlayback,
  startAutoPlayback,
  stopPlayback,
  updateTempo,
} from '../modules/tone';
import { buildBemClassName } from '../modules/util';
import { AppContext } from '../App';
import { useTranslation } from 'react-i18next';

// Everything the continuous auto playback needs; null when auto mode is off.
export interface AutoConfig {
  nextMeasures: Measure[];
  requestNextMeasures: MeasuresProvider;
  onAdvance: AdvanceHandler;
}

export interface PlayerProps {
  measures: Measure[];
  playbackState: PlaybackState;
  metronomeOn: boolean;
  repeat: number;
  autoConfig: AutoConfig | null;
  className?: string;
  onPlaybackStateChange: PlaybackStateChangeHandler;
  onNoteTrigger: NoteTriggerHandler;
  onMetronomeClickTrigger: NoteTriggerHandler;
}

export type PlaybackStateChangeHandler = (playbackState: PlaybackState) => void;

const buildClassName = buildBemClassName('c-rr-player');

const Player = ({
  measures,
  playbackState,
  metronomeOn,
  repeat,
  autoConfig,
  className = '',
  onPlaybackStateChange,
  onNoteTrigger,
  onMetronomeClickTrigger,
}: PlayerProps) => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);

  const {
    tempo,
    pitch,
    timeSignature,
    metronomeSettings,
  } = state.scoreSettings;

  const stopHandler = () => {
    onPlaybackStateChange(PlaybackState.STOPPED);
    Transport.off('stop', stopHandler);
  };

  // Set tempo when it changes
  useEffect(() => {
    updateTempo(tempo);
  }, [tempo]);

  const handlePlayToggle = () => {
    if (playbackState === PlaybackState.STOPPED) {
      if (autoConfig) {
        startAutoPlayback(
          measures,
          autoConfig.nextMeasures,
          pitch,
          timeSignature,
          metronomeSettings,
          onNoteTrigger,
          onMetronomeClickTrigger,
          repeat,
          autoConfig.requestNextMeasures,
          autoConfig.onAdvance
        );
      } else {
        startPlayback(
          measures,
          pitch,
          timeSignature,
          metronomeSettings,
          onNoteTrigger,
          onMetronomeClickTrigger,
          repeat
        );
      }
      onPlaybackStateChange(PlaybackState.PLAYING);
      Transport.on('stop', stopHandler);
    } else {
      stopPlayback();
    }
  };

  return (
    <div className={buildClassName()()}>
      <button
        type="button"
        className={classnames(
          className,
          'c-rr-button',
          'c-rr-button--dark',
          buildClassName('play-toggle')()
        )}
        onClick={handlePlayToggle}
        disabled={metronomeOn}
      >
        {playbackState === PlaybackState.PLAYING
          ? t('stopPlayback')
          : t('startPlayback')}
      </button>
    </div>
  );
};

export default Player;
